import { useI18n } from 'i18n-utils'
import { DialogHeader } from '../components/DialogHeader'
import { HocElDialog } from '../hoc-element-components/HocElDialog'
import { DefineComponentProps } from 'utils'
import { OrderDirection } from 'network'
import { useProjectTheme } from 'store'

import { CompareIcon, CustomButton, InputView, ListCell, Tag, ToggleIcon } from '..'
import { useUpdateMargin } from '../stores/use-update-margin'
import { PositionInfoLayout } from '../layouts/PositionInfo.layout'
import './UpdateMargin.scss'

export type UpdateMarginProps = {
  position: ApiResponse.DoubleContractPosition
  onClose?: () => void
}

export const UpdateMargin = defineComponent<UpdateMarginProps>({
  props: DefineComponentProps<UpdateMarginProps>(['onClose', 'position']),
  name: 'UpdateMargin',
  setup(props) {
    const { t } = useI18n()
    const updateMargin = useUpdateMargin(props.position)
    const themeStore = useProjectTheme()
    const theme = computed(() => themeStore.theme)

    onBeforeMount(() => {
      updateMargin.open()
    })

    const onClose = () => {
      if (!updateMargin.updateMarginVisible.value) return
      updateMargin.close()
      props.onClose?.()
    }

    const closeName = computed(() => {
      if (props.position.direction === OrderDirection.Buy) {
        return t('做多')
      } else {
        return t('做空')
      }
    })

    const onConfirm = async () => {
      await updateMargin.onUpdateMargin()
      onClose()
    }

    const newLiquidationPrice = computed(() => {
      if (updateMargin.newLiquidationPrice.value.isLessThanOrEqualTo(0)) {
        return '--'
      }
      return updateMargin.newLiquidationPrice.value.formatNumberWithCommas()
    })

    const newMargin = computed(() => {
      if (updateMargin.newMargin.value.isLessThanOrEqualTo(0)) {
        return 0
      }
      return updateMargin.newMargin.value.formatNumberWithCommas()
    })

    return () => (
      <HocElDialog width={520} v-model={updateMargin.updateMarginVisible.value} showClose={false} onClose={onClose}>
        {{
          header: () => <DialogHeader onClose={onClose}>{t('调整保证金')}</DialogHeader>,
          default: () => (
            <div class="update-margin-layout">
              <PositionInfoLayout>
                <div class="coin-info">
                  <div class="icon-wrapper">
                    <img src={updateMargin.getSymbolIcon()} alt="coin" />
                  </div>
                  <div>
                    <div class="coin-name">
                      {updateMargin.symbolInfo.value?.symbolName?.toUpperCase()}
                      {t('永续')}
                    </div>
                    <div class="position-info">
                      <Tag color={props.position.direction === OrderDirection.Buy ? theme.value.colors.up : theme.value.colors.down}>{closeName.value}</Tag>
                      <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                        {props.position.marginType === 1 ? t('逐仓') : t('全仓')}
                        {props.position.leverage}X
                      </Tag>
                    </div>
                  </div>
                </div>
                <div class="position-info-list">
                  <ListCell title={t('开仓价格')} description={props.position.holdPrice.formatNumberWithCommas()} />
                  <ListCell title={t('标记价格')} description={updateMargin.markPrice.value?.formatNumberWithCommas()} />
                </div>
              </PositionInfoLayout>

              <div class="position-form">
                {/* 委托类型 */}
                <div class="position-form-item type">
                  <span class="place-order-type">{t('调整类型')}</span>
                  <span class="place-order-type-value" onClick={updateMargin.switchMarginType}>
                    <ToggleIcon />
                    {t(updateMargin.addSide.value ? '增加' : '减少')}
                  </span>
                </div>
                <div class="position-form-item">
                  <InputView placeholder={t('数量')} isFloatNumber maxDecimalLength={2} v-model={updateMargin.amount.value} radius={6} suffixIcon={() => 'USDT'} />
                  <div class="position-form-item-amount-info">
                    <span>{t(updateMargin.addSide.value ? '可用' : '可减少')}</span>
                    <span class="amount-value">
                      {updateMargin.addSide.value ? updateMargin.availableMargin.value.formatCommonMoney() : updateMargin.canReduce.value.formatCommonMoney()}
                    </span>
                    <span class="all-btn" onClick={updateMargin.onAll}>
                      {t('全部')}
                    </span>
                  </div>
                </div>

                <div class="info-wrapper">
                  <div class="margin-info">
                    <div class="margin-price">
                      <div class="margin-price-item current-price">
                        <span class="margin-price-item-title">{t('当前保证金')}</span>
                        <span class="margin-price-item-value">{props.position.margin.formatNumberWithCommas()}</span>
                      </div>
                      <CompareIcon />
                      <div class="margin-price-item new-price">
                        <span class="margin-price-item-title">{t('调整后保证金')}</span>
                        <span class="margin-price-item-value">{newMargin.value}</span>
                      </div>
                    </div>
                  </div>

                  <div class="margin-info">
                    <div class="margin-price">
                      <div class="margin-price-item current-price">
                        <span class="margin-price-item-title">{t('当前强平价')}</span>
                        <span class="margin-price-item-value">{props.position.liquidationPrice.formatNumberWithCommas()}</span>
                      </div>
                      <CompareIcon />
                      <div class="margin-price-item new-price">
                        <span class="margin-price-item-title">{t('调整后强平价')}</span>
                        <span class="margin-price-item-value">{newLiquidationPrice.value}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="close-btn">
                  <CustomButton type={'primary'} loading={updateMargin.loading.value} onClick={onConfirm}>
                    {t('确认')}
                  </CustomButton>
                </div>
              </div>
            </div>
          ),
        }}
      </HocElDialog>
    )
  },
})
